import './styles.scss'
import React, { memo } from 'react'
import Lottie from 'lottie-react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from 'primereact/skeleton'
import { InputText } from 'primereact/inputtext'
import { SKELETON_COUNT } from 'Utils/WeatherUtils/Constants'
import { LocationCardSkeletonInner } from '../Skeleton'
import { LocationCard } from '../LocationCard'
import { LocationNoDataCard } from '../NoDataCard'

export const WeatherLocationsColumn = memo(({ isLoadingLocations, isSearching, isOtherOptionsOpen, selectedCountry, selectedState, selectedRegion, weatherData, setWeatherData, currentCity, otherLocationsData, savedLocationsFound, searchQuery, userDetails, onLocationClick, onCountrySearch, onAddLocation, onRemoveLocation, getCountryFlag, getCurrentDateForTimezone, getCurrentTimeForTimezone, getWeatherConditionIcon, getWeatherLottieAsset }) => {
  const { t } = useTranslation()

  if (isOtherOptionsOpen) {
    return (
      <div className="weather-locations-column other-locations-column">
        <div className="locations-list other-locations-list">
          {isLoadingLocations && !isSearching ? (
            <>
              <div className="column-header-skeleton">
                <Skeleton width="90%" height="18px" className="skeleton-text custom-skelton" />
              </div>
              {Array.from({ length: SKELETON_COUNT.LOCATION_CARD }).map((_, index) => (
                <LocationCardSkeletonInner key={index} />
              ))}
            </>
          ) : (
            <>
              <div className="weather-search-container">
                <div className="search-input-wrapper p-input-icon-left">
                  <i className="pi pi-search" data-testid="search-icon" />
                  <InputText type="text" name="search-city" id="search-city-input" placeholder="Search City" value={searchQuery} onChange={onCountrySearch} className="weather-search-input" />
                  {isSearching && <i className="pi pi-spinner pi-spin" style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }} />}
                </div>
              </div>
              {isSearching ? (
                <>
                  {Array.from({ length: SKELETON_COUNT.LOCATION_CARD }).map((_, index) => (
                    <LocationCardSkeletonInner key={index} />
                  ))}
                </>
              ) : (
                <>
                  {savedLocationsFound ? (
                    <>
                      {otherLocationsData?.map((item) => {
                        const locationDateTime = item?.timezone ? new Date(new Date().toLocaleString('en-US', { timeZone: item.timezone })) : new Date()

                        const backgroundAnimation = getWeatherLottieAsset(item?.weather?.weather_condition, locationDateTime)
                        const isBackgroundImage = typeof backgroundAnimation === 'string'

                        return (
                          <div key={item.id} className={`otherlocation-card ${currentCity === item?.location_name ? 'active' : ''} other-locations-card`} onClick={() => onLocationClick(item?.location_id || item?.id)}>
                            <div className="otherlocation-card-background">{isBackgroundImage ? <img src={backgroundAnimation} alt="Weather background" className="weather-background-animation" /> : <Lottie animationData={backgroundAnimation} loop={true} className="weather-background-animation" />}</div>
                            <div className="otherlocation-card-content">
                              <div className="otherlocation-card-header">
                                <div className="otherlocation-date-time-wrapper">
                                  <div className="otherlocation-date-time-container">
                                    <div className="otherlocation-date">{getCurrentDateForTimezone(item?.timezone, 'spaceFormat')}</div>
                                    <div className="otherlocation-date">{getCurrentTimeForTimezone(item?.timezone)}</div>
                                  </div>
                                </div>
                                <div className="location-flag-wrapper">
                                  <img src={getCountryFlag(item?.country_code)} alt={item?.country_name} className="otherlocation-flag" />
                                  <span className="location-country">{item?.country_name}</span>
                                </div>
                                <div className="otherlocation-temp-large">
                                  <div className="otherlocation-radio-wrapper">{currentCity === item?.location_name && <input type="radio" name="location" value={item?.location_name} defaultChecked />}</div>
                                  <div className="otherlocation-temp-large-value">{Number(item?.weather?.temperature).toFixed(0)}°C</div>
                                </div>
                              </div>

                              <div className="otherlocation-card-body">
                                <div className="add-remove-btn">
                                  {item?.isUserSaved ? (
                                    <div className="remove-location-btn" onClick={(e) => onRemoveLocation(userDetails?.id, item?.location_id, item?.location_name, true, e)}>
                                      <i className="pi pi-minus-circle" />
                                    </div>
                                  ) : (
                                    <div className="add-location-btn" onClick={(e) => onAddLocation(e, item, true)}>
                                      <i className="pi pi-plus-circle" />
                                    </div>
                                  )}
                                </div>
                                <div className="otherlocation-city">
                                  {item?.location_name}
                                  {item?.zone && (
                                    <span className="otherlocation-zone">
                                      {t('ZONE')} {item?.zone}
                                    </span>
                                  )}
                                </div>
                                <div></div>
                              </div>

                              <div className=" other-locations-card-footer">
                                <div className="other-location-condition">{item?.weather?.weather_condition}</div>
                                <div className="other-location-high-low">
                                  H:{Number(item?.weather?.high_temperature).toFixed(0)}°C L:{Number(item?.weather?.low_temperature).toFixed(0)}°C
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </>
                  ) : (
                    <LocationNoDataCard />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="weather-locations-column">
      <div className="locations-list">
        {isLoadingLocations ? (
          <>
            <div className="column-header-skeleton">
              <Skeleton width="90%" height="18px" className="skeleton-text custom-skelton" />
            </div>
            {Array.from({ length: SKELETON_COUNT.LOCATION_CARD }).map((_, index) => (
              <LocationCardSkeletonInner key={index} />
            ))}
          </>
        ) : (
          <>
            <div className="state-block">
              <div className="coffee-producing-header">
                <div className="coffee-producing-content">
                  <img src={getCountryFlag(selectedCountry?.country_code)} alt={selectedCountry?.country_name} className="location-flag" />
                  <div className="coffee-producing-header-content-text">
                    <span className="location-country">
                      {selectedCountry?.country_name} {selectedState?.state && `- ${selectedState?.state}`}
                      {selectedRegion?.region && `, ${selectedRegion?.region}`}
                    </span>
                  </div>
                </div>
              </div>
              {weatherData.map((item) => (
                <LocationCard key={item?.location_id || item?.id} item={item} isActive={currentCity === item?.location_name} onLocationClick={onLocationClick} getCurrentDateForTimezone={getCurrentDateForTimezone} getCurrentTimeForTimezone={getCurrentTimeForTimezone} getWeatherConditionIcon={getWeatherConditionIcon} getWeatherLottieAsset={getWeatherLottieAsset} onAddLocation={onAddLocation} onRemoveLocation={onRemoveLocation} userDetails={userDetails} weatherData={weatherData} setWeatherData={setWeatherData} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
})
WeatherLocationsColumn.displayName = 'WeatherLocationsColumn'
